import React, { useState } from 'react';

// Mock data for demonstration
const ballByBall = [
  {
    innings: 1,
    battingTeam: 'India',
    overs: [
      [
        { ball: '0.1', type: 'dot', runs: 0 },
        { ball: '0.2', type: 'six', runs: 6 },
        { ball: '0.3', type: 'not_bowled' },
        { ball: '0.4', type: 'not_bowled' },
        { ball: '1.0', type: 'not_bowled' },
      ],
      [
        { ball: '1.1', type: 'not_bowled' },
        { ball: '1.2', type: 'not_bowled' },
        { ball: '1.3', type: 'not_bowled' },
        { ball: '1.4', type: 'not_bowled' },
        { ball: '2.0', type: 'not_bowled' },
      ],
      [
        { ball: '2.1', type: 'not_bowled' },
        { ball: '2.2', type: 'not_bowled' },
        { ball: '2.3', type: 'not_bowled' },
        { ball: '2.4', type: 'not_bowled' },
        { ball: '3.0', type: 'not_bowled' },
      ],
    ],
  },
  {
    innings: 2,
    battingTeam: 'Australia',
    overs: [
      [
        { ball: '0.1', type: 'run', runs: 1 },
        { ball: '0.2', type: 'dot', runs: 0 },
        { ball: '0.3', type: 'four', runs: 4 },
        { ball: '0.4', type: 'wicket' },
        { ball: '1.0', type: 'six', runs: 6 },
      ],
      [
        { ball: '1.1', type: 'dot', runs: 0 },
        { ball: '1.2', type: 'run', runs: 2 },
        { ball: '1.3', type: 'dot', runs: 0 },
        { ball: '1.4', type: 'overthrow', runs: 1 },
        { ball: '2.0', type: 'not_bowled' },
      ],
      [
        { ball: '2.1', type: 'not_bowled' },
        { ball: '2.2', type: 'not_bowled' },
        { ball: '2.3', type: 'not_bowled' },
        { ball: '2.4', type: 'not_bowled' },
        { ball: '3.0', type: 'not_bowled' },
      ],
    ],
  },
];

const BALL_COLORS = {
  not_bowled: 'bg-gray-200',
  dot: 'bg-gray-400 text-white',
  run: 'bg-blue-500 text-white',
  four: 'bg-green-500 text-white',
  six: 'bg-emerald-500 text-white',
  wicket: 'bg-red-500 text-white',
  wide: 'bg-yellow-400',
  no_ball: 'bg-orange-400',
  bye: 'bg-orange-200',
  leg_bye: 'bg-orange-300',
  overthrow: 'bg-purple-400 text-white',
  multi: 'bg-indigo-400 text-white',
};

const BALL_TYPE_LABELS = {
  not_bowled: 'Not Bowled Yet',
  dot: 'Dot Ball',
  run: 'Regular Run(s)',
  four: 'Four (Boundary)',
  six: 'Six (Boundary)',
  wicket: 'Wicket',
  wide: 'No Ball / Wide',
  no_ball: 'No Ball',
  bye: 'Bye',
  leg_bye: 'Leg Bye',
  overthrow: 'Overthrow',
  multi: 'Multi Score Types',
};

const BallByBallOverview: React.FC = () => {
  const [openInnings, setOpenInnings] = useState([true, false]);

  const toggleInnings = (idx: number) => {
    setOpenInnings(prev => prev.map((open, i) => (i === idx ? !open : open)));
  };

  return (
    <div className="bg-white rounded-xl shadow border p-4 mt-6">
      <div className="font-bold text-lg mb-4">Match Overview</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {ballByBall.map((innings, idx) => (
          <div key={innings.innings} className="mb-4">
            <div
              className="font-semibold text-base mb-2 flex items-center cursor-pointer select-none"
              onClick={() => toggleInnings(idx)}
            >
              <span>{innings.innings === 1 ? '1st' : '2nd'} Innings - {innings.battingTeam}</span>
              {idx === ballByBall.length - 1 && (
                <span className="ml-2 px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Current</span>
              )}
              <span className="ml-2 text-gray-400">{openInnings[idx] ? '▼' : '▲'}</span>
            </div>
            {openInnings[idx] && (
              <div>
                {innings.overs.map((over, oIdx) => (
                  <div key={oIdx} className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-500 mr-2">Over 0{oIdx + 1}</span>
                    {over.map((ball, bIdx) => (
                      <button
                        key={ball.ball}
                        className={`w-10 h-10 rounded-lg flex flex-col items-center justify-center font-bold text-xs border ${BALL_COLORS[ball.type] || 'bg-gray-200'}`}
                        style={{ transition: 'background 0.2s, color 0.2s' }}
                        title={BALL_TYPE_LABELS[ball.type]}
                        disabled
                      >
                        <span>{ball.ball}</span>
                        {ball.runs !== undefined && <span className="text-[10px]">{ball.runs}</span>}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 border-t pt-4">
        <div className="font-semibold mb-2 text-sm">Ball Button Color Legend</div>
        <div className="flex flex-wrap gap-3 text-xs">
          {Object.entries(BALL_TYPE_LABELS).map(([type, label]) => (
            <div key={type} className="flex items-center gap-2">
              <span className={`inline-block w-4 h-4 rounded ${BALL_COLORS[type] || 'bg-gray-200'}`}></span>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BallByBallOverview; 